import type {Validator} from 'svelte-forms';
import {isDefined, isNullOrUndefined} from '../../../../utils/custom-utils';
import type {NumberRangeValueEditorModel, NumberRangeValueEditorValidations} from './types';
import {LocalizationAwareError} from '../../errors/localization-aware-error';

export function validatorFactory(editorName: string, validations: NumberRangeValueEditorValidations): Validator[] {
    const validators: Validator[] = [];

    if (isDefined(validations.required)) {
        validators.push(required(editorName));
    }

    if (isDefined(validations.min)) {
        validators.push(min(editorName, validations.min));
    }

    if (isDefined(validations.max)) {
        validators.push(max(editorName, validations.max));
    }

    if (isDefined(validations.fromBiggerThanTo)) {
        validators.push(fromBiggerThanTo(editorName));
    }

    if (isDefined(validations.toBiggerThanFrom)) {
        validators.push(toBiggerThanFrom(editorName));
    }

    return validators;
}

export function throwParseErrorIfNaN(value: number | null): number {
    if (Number.isNaN(value)) {
        throw new LocalizationAwareError('number', 'Parsed value is not number');
    }
    return value;
}

function required(editorName: string): Validator {
    return (value: NumberRangeValueEditorModel) => {
        return ({valid: Number.isInteger(value[`${editorName}-from`]) && Number.isInteger(value[`${editorName}-to`]), name: 'required'})
    };
}

function min(editorName: string, minValue: number): Validator {
    return (value: NumberRangeValueEditorModel) => {
        const from = value[`${editorName}-from`];
        const to = value[`${editorName}-to`];
        return ({valid: (isNullOrUndefined(from) || from >= minValue && (isNullOrUndefined(to) || to >= minValue)), name: 'min'});
    };
}

function max(editorName: string, maxValue: number): Validator {
    return (value: NumberRangeValueEditorModel) => {
        const from = value[`${editorName}-from`];
        const to = value[`${editorName}-to`];
        return ({valid: (isNullOrUndefined(from) || from <= maxValue && (isNullOrUndefined(to) || to <= maxValue)), name: 'max'});
    };
}

function fromBiggerThanTo(editorName: string): Validator {
    return (value: NumberRangeValueEditorModel) => {
        const from = value[`${editorName}-from`];
        const to = value[`${editorName}-to`];
        return ({valid: (from > to), name: 'fromBiggerThanTo'});
    };
}

function toBiggerThanFrom(editorName: string): Validator {
    return (value: NumberRangeValueEditorModel) => {
        const from = value[`${editorName}-from`];
        const to = value[`${editorName}-to`];
        return ({valid: (to > from), name: 'toBiggerThanFrom'});
    };
}

